import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { ZoomIn, ZoomOut, RotateCcw, Loader2 } from 'lucide-react';

// Helper to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size to desired output size
  const size = 256; // Output size for avatar
  canvas.width = size;
  canvas.height = size;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  );

  // Return as blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

export const AvatarCropper = ({ 
  open, 
  onOpenChange, 
  onSave, 
  currentAvatar 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      });
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      await onSave(croppedBlob);
      handleClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onOpenChange(false);
  };

  const resetZoom = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">Change your picture</DialogTitle>
          <DialogDescription className="text-ink-55">
            {imageSrc
              ? 'Drag to move it. Use the slider to zoom.'
              : 'Choose an image for your profile.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {!imageSrc ? (
            // Upload area
            <div className="flex flex-col items-center gap-4">
              {currentAvatar ? (
                <div className="w-32 h-32 rounded-full overflow-hidden bg-avatar">
                  <img 
                    src={currentAvatar} 
                    alt="Current avatar" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div aria-hidden="true" className="w-32 h-32 rounded-full bg-avatar" />
              )}
              
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <span className="inline-flex items-center justify-center min-h-[44px] px-[22px] py-[11px] bg-inset text-ink rounded-md text-[15px] font-semibold hover:bg-rule transition-colors duration-state">
                  Choose an image
                </span>
              </label>
              <p className="text-sm text-ink-55">JPG, PNG or GIF, up to 5 MB.</p>
            </div>
          ) : (
            // Cropper area
            <div className="space-y-4">
              <div className="relative w-full h-64 bg-ink rounded-lg overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>

              {/* Zoom controls */}
              <div className="flex items-center gap-4 px-2">
                <ZoomOut className="w-4 h-4 text-ink-55" aria-hidden="true" />
                <Slider
                  value={[zoom]}
                  onValueChange={([value]) => setZoom(value)}
                  min={1}
                  max={3}
                  step={0.1}
                  className="flex-1"
                />
                <ZoomIn className="w-4 h-4 text-ink-55" aria-hidden="true" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={resetZoom}
                  className="ml-2"
                  aria-label="Reset zoom and position"
                  title="Reset"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {/* Change image button */}
              <div className="text-center">
                <label className="cursor-pointer text-[15px] font-medium text-brand hover:underline">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  Choose a different image
                </label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          {imageSrc && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarCropper;
